/**
 * Exports API client
 * Handles data export requests and download URLs
 */

import { apiClient } from './client';

// ============================================================================
// Types
// ============================================================================

export type ExportEntityType =
  | 'participants'
  | 'coaches'
  | 'partners'
  | 'cohorts'
  | 'groups'
  | 'sessions'
  | 'resources'
  | 'users'
  | 'survey_responses'
  | 'attendance';

export type ExportContextType = 'global' | 'cohort' | 'partner';

export type ExportRequestStatus = 'pending' | 'processing' | 'completed' | 'failed';

export type ExportColumnCategory =
  | 'basic'
  | 'contact'
  | 'work'
  | 'cohort'
  | 'intake'
  | 'profile'
  | 'status'
  | 'capacity'
  | 'dates'
  | 'config'
  | 'counts'
  | 'access'
  | 'scheduling'
  | 'identity'
  | 'professional'
  | 'learning'
  // MDF-214: cohort custom fields (participants only).
  | 'custom_fields'
  // MED-1234: synthetic categories for grid-only columns surfaced in Custom
  // selection (e.g. dynamic per-question survey columns) that have no backend
  // registry entry.
  | 'survey_questions'
  | 'additional';

export interface ExportColumnDefinition {
  key: string;
  label: string;
  category: ExportColumnCategory;
  field?: string;
  formatter?: 'date' | 'datetime' | 'boolean' | 'currency' | 'number' | 'array';
}

export interface CreateExportRequest {
  entity_type: ExportEntityType;
  context_type?: ExportContextType;
  context_id?: string;
  filter_context?: Record<string, unknown>;
  selected_columns?: string[];
  include_all_columns?: boolean;
  match_current_view?: boolean;
  format?: 'csv' | 'xlsx';
  populate_dnm_groupings?: boolean;
}

export interface ExportRequest {
  id: string;
  entity_type: ExportEntityType;
  context_type: ExportContextType;
  context_id: string | null;
  filter_context: Record<string, unknown> | null;
  selected_columns: string[];
  include_all_columns: boolean;
  match_current_view: boolean;
  format: 'csv' | 'xlsx';
  requested_by: string;
  requested_at: string;
  status: ExportRequestStatus;
  retry_count: number;
  started_at: string | null;
  completed_at: string | null;
  record_count: number | null;
  file_size: number | null;
  expires_at: string | null;
  error_message: string | null;
  created_at: string;
  // With details
  requested_by_name?: string;
  requested_by_email?: string;
  context_name?: string | null;
}

export interface ExportDownloadResponse {
  download_url: string;
  filename: string;
  file_size: number;
  expires_at: string;
}

export interface ExportPreset {
  label: string;
  description: string;
  columns: string[];
}

export interface AvailableColumnsResponse {
  entity_type: ExportEntityType;
  columns: ExportColumnDefinition[];
  categories: { key: ExportColumnCategory; label: string }[];
  presets?: Record<string, ExportPreset>;
}

export interface ExportPendingCount {
  pending_count: number;
  max_pending: number;
}

export interface MatchingToolValidation {
  total_participants: number;
  missing_availability_count: number;
  missing_availability_participants: { first_name: string; last_name: string }[];
}

export interface DnmGroupingsValidation {
  total_groups: number;
  total_participants_with_groupings: number;
  total_participants: number;
  missing_manager_count: number;
  missing_manager_participants: { first_name: string; last_name: string }[];
}

// ============================================================================
// API Functions
// ============================================================================

export const exportsApi = {
  /**
   * Create a new export request
   */
  create: (data: CreateExportRequest): Promise<{ id: string; status: ExportRequestStatus }> =>
    apiClient.post('/api/exports', data),

  /**
   * Get export request by ID
   */
  getById: (id: string): Promise<ExportRequest> =>
    apiClient.get(`/api/exports/${id}`),

  /**
   * List user's export requests
   */
  list: (params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
    entity_type?: ExportEntityType;
    status?: ExportRequestStatus;
  }): Promise<{ data: ExportRequest[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> =>
    apiClient.getPaginated('/api/exports', params),

  /**
   * Get download URL for completed export
   */
  getDownloadUrl: (id: string): Promise<ExportDownloadResponse> =>
    apiClient.get(`/api/exports/${id}/download`),

  /**
   * Get available columns for an entity type
   */
  getAvailableColumns: (entityType: ExportEntityType, cohortIds: string[] = []): Promise<AvailableColumnsResponse> =>
    apiClient.get(
      `/api/exports/columns/${entityType}${cohortIds.length > 0 ? `?cohort_ids=${cohortIds.join(',')}` : ''}`,
    ),

  /**
   * Get pending export count (for rate limiting display)
   */
  getPendingCount: (): Promise<ExportPendingCount> =>
    apiClient.get('/api/exports/pending-count'),

  /**
   * Validate matching tool export - check for participants missing availability data
   */
  validateMatchingTool: (
    contextType: ExportContextType,
    contextId: string,
    filterContext?: Record<string, unknown>,
  ): Promise<MatchingToolValidation> =>
    apiClient.post('/api/exports/validate/matching-tool', {
      context_type: contextType,
      context_id: contextId,
      filter_context: filterContext,
    }),

  /**
   * Validate DNM groupings - check for participants missing manager data
   */
  validateDnmGroupings: (
    contextType: ExportContextType,
    contextId: string,
    filterContext?: Record<string, unknown>,
  ): Promise<DnmGroupingsValidation> =>
    apiClient.post('/api/exports/validate/dnm-groupings', {
      context_type: contextType,
      context_id: contextId,
      filter_context: filterContext,
    }),
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get human-readable label for entity type
 */
export const getEntityLabel = (entityType: ExportEntityType): string => {
  const labels: Record<ExportEntityType, string> = {
    participants: 'Participants',
    coaches: 'Coaches',
    partners: 'Partners',
    cohorts: 'Cohorts',
    groups: 'Groups',
    sessions: 'Sessions',
    resources: 'Resources',
    users: 'Users',
    survey_responses: 'Survey Responses',
    attendance: 'Attendance',
  };
  return labels[entityType] || entityType;
};

/**
 * Get human-readable label for status
 */
export const getStatusLabel = (status: ExportRequestStatus): string => {
  const labels: Record<ExportRequestStatus, string> = {
    pending: 'Pending',
    processing: 'Processing',
    completed: 'Completed',
    failed: 'Failed',
  };
  return labels[status] || status;
};

/**
 * Get status color for UI
 */
export const getStatusColor = (status: ExportRequestStatus): string => {
  const colors: Record<ExportRequestStatus, string> = {
    pending: 'yellow',
    processing: 'blue',
    completed: 'green',
    failed: 'red',
  };
  return colors[status] || 'gray';
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export default exportsApi;
